import * as flexsearch from "flexsearch";
import fflate from "fflate";
import EnglishPreset from "flexsearch/lang/en";
import GermanPreset from "flexsearch/lang/de";
import FrenchPreset from "flexsearch/lang/fr";

/**
 * Returns a flexsearch encoder for the given language.
 * Currently supported languages are: en, de, fr.
 */
export function getEncoder(lang: string = "en"): flexsearch.Encoder {
    switch (lang) {
        case "en":
            return new flexsearch.Encoder(EnglishPreset);
        case "de":
            return new flexsearch.Encoder(GermanPreset);
        case "fr":
            return new flexsearch.Encoder(FrenchPreset);
    }

    return new flexsearch.Encoder(flexsearch.Charset.LatinSoundex);
}

/**
 * Pulls all documents from the index, equal to a "get everything" query.
 */
export function getAllDocuments<T extends flexsearch.DocumentData>(index: flexsearch.Document<T>): T[] {
    return Array.from((index as unknown as { store: Map<string, T> }).store.entries())
        .map(([id, doc]) => ({ id, ...doc }));
}

/**
 * Use this as the first step in your search.
 * **DO NOT USE flexsearch limit and tags, they don't work as expected.**
 */
export function search<T extends flexsearch.DocumentData>({
    index,
    query,
    fields
}: {
    index: flexsearch.Document<T>,
    query: string,
    fields?: flexsearch.FieldName<T>[]
}): T[] {
    return _flattenSearchResults(index.search({
        query,
        limit: Number.MAX_SAFE_INTEGER,
        enrich: true,
        ...(fields?.length ? {
            index: fields
        } : {})
    }));
}

function _flattenSearchResults<T extends flexsearch.DocumentData>(results: flexsearch.EnrichedDocumentSearchResults<T>): T[] {
    const response: T[] = [];

    // Being smart, respecting each field order, returning A:1, B:1, A:2, B:2, A:3, B:3, etc. instead of A:1, A:2, A:3, B:1, B:2, B:3
    const maxFieldMatches = Math.max(...results.map(result => result.result.length));
    for (let i = 0; i < maxFieldMatches; i++) {
        results.forEach(result => {
            const id = result.result[i]?.id;
            const doc = result.result[i]?.doc;
            if (id && doc && !response.find(doc => doc.id === id)) {
                response.push({
                    ...doc,
                    id
                });
            }
        });
    }

    return response;
}

/**
 * Fetches and decompresses the index data from the given URL, returning a flexsearch index data object.
 */
export async function fetchCompressedIndexData(indexFileReadUrl: string) {
    const response = await fetch(indexFileReadUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch index data from ${indexFileReadUrl}: ${response.statusText}`);
    }
    const data = await response.arrayBuffer();
    const compressed = new Uint8Array(data);
    const decompressed = await new Promise<Uint8Array>((resolve, reject) => {
        fflate.decompress(compressed, (err, data) => {
            if (err) {
                reject(err);
                return;
            }
            resolve(data);
        });
    });
    return JSON.parse(fflate.strFromU8(decompressed)) as { key: string, data: string }[];
}
